import styles from '../map.module.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import {
    useCoastalSegmentGroups,
    useSetCoastalSegmentGroups,
    useSelectedCoastalSegmentGroup,
    useSetSelectedCoastalSegmentGroup,
    useSetFeatureMouseOver,
    usePilotCertificateCoastalSegmentPerson,
    usePilotCertificateToggleFeatures,
    useSetPilotCertificateToggleFeatures,
    useSetSelectedPilotCoastalSegmentGroup
} from "../hooks/useMapStore";
import {
    usePilotCertificateCoastalSegmentGroupsEvent
} from "components/hooks/useAppStore";
import { Grid, GridColumn as Column, GridCellProps, GridNoRecords } from '@progress/kendo-react-grid';
import { IconPicker } from 'components/layout/icons/IconPicker';
import Spacer from 'components/layout/Spacer';
import MapPropertiesService from '../services/MapPropertiesService';
import { Checkbox } from '@progress/kendo-react-inputs';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridWrapper } from 'components/kendo/GridWrapper';

export const MapPanelPilotCertificateCoastalSegments = () => {

    const coastalSegmentGroups = useCoastalSegmentGroups();
    const setCoastalSegmentGroups = useSetCoastalSegmentGroups();
    const selectedCoastalSegmentGroup = useSelectedCoastalSegmentGroup();
    const pilotCertificateCoastalSegmentGroupsEvent = usePilotCertificateCoastalSegmentGroupsEvent();
    const pilotCertificateCoastalSegmentPerson = usePilotCertificateCoastalSegmentPerson();
    const pilotCertificateToggleFeatures = usePilotCertificateToggleFeatures();
    const setPilotCertificateToggleFeatures = useSetPilotCertificateToggleFeatures();

    const defaultFilter = { field: 'status', operator: 'eq', value: "READY_FOR_APPROVAL" }
    const pilotCertificatePersonMode = pilotCertificateCoastalSegmentPerson?.mode ?? "NONE";
    
    const [canSave, setCanSave] = useState(true);
    const [filter, setFilter] = useState<CompositeFilterDescriptor>();
    
    const buttonText = useMemo(
        () => (pilotCertificateCoastalSegmentPerson?.mode === "APPROVE") ? "Godkjenn" : "Lagre",
        [pilotCertificateCoastalSegmentPerson?.mode]
    );
    const gridStyleHeight = useMemo(
        () => (pilotCertificateCoastalSegmentPerson?.mode === "APPROVE" ) ?
             "calc(100vh - 193px)" :
             "calc(100vh - 165px)",
        [pilotCertificateCoastalSegmentPerson?.mode]
    );
    
    const handleSave = () => {
        const ids = coastalSegmentGroups.map(group => group.id);
        pilotCertificateCoastalSegmentGroupsEvent(ids);
    }

    useEffect(() => {
        if(!coastalSegmentGroups || coastalSegmentGroups.length === 0) return;
        setTimeout(() => {
            setPilotCertificateToggleFeatures(pilotCertificateCoastalSegmentPerson?.mode === "APPROVE");
        }, 500);
    }, [coastalSegmentGroups, pilotCertificateCoastalSegmentPerson]);

    useEffect(() => {
        if (!selectedCoastalSegmentGroup) return;

        const existing = coastalSegmentGroups.find(
            group => group.id === selectedCoastalSegmentGroup.id
        );

        if (selectedCoastalSegmentGroup.isSelected) {
            if (existing) return;
            
            const updated = [...coastalSegmentGroups];
            updated.push(selectedCoastalSegmentGroup);
            setCoastalSegmentGroups(updated);

        } else {
            if (!existing) return;
            const updated = coastalSegmentGroups.filter(
                group => group.id !== selectedCoastalSegmentGroup.id
            );
            setCoastalSegmentGroups(updated);
        }

        if (canSave) return;
        setCanSave(true);
    }, [selectedCoastalSegmentGroup]);

    useEffect(() => {
        if (pilotCertificateToggleFeatures) {
            setFilter(defaultFilter);
        } else {
            setFilter(null);
        }
    }, [pilotCertificateToggleFeatures]);

    return (
        <div className={styles.panelPilotCertificate}>
            <div>
                {
                    (pilotCertificatePersonMode === "APPROVE") &&
                    <>
                        <Checkbox
                            label="Vis kun omr&aring;der til godkjenning"
                            value={pilotCertificateToggleFeatures}
                            onChange={() => {
                                setPilotCertificateToggleFeatures(!pilotCertificateToggleFeatures);
                            }}
                        />
                        <Spacer height={10} />
                    </>
                }
            </div>
            <GridWrapper>
                <Grid
                    style={{ height: gridStyleHeight }}
                    data={coastalSegmentGroups}
                    dataItemKey="id"
                    sortable={true}
                    autoProcessData={true}
                    filter={filter}
                    defaultSort={[{ field: 'name', dir: 'asc' }]}
                    selectable={{
                        enabled: true,
                        mode: 'single'
                    }}>
                    <GridNoRecords>
                        Ingen valgte omr&aring;der
                    </GridNoRecords>
                    <Column
                        field="status"
                        title=" "
                        width="8px"
                        cell={StatusCell}
                    />
                    <Column
                        field="name"
                        title="Omr&aring;de"
                        cell={NameCell}
                    />
                    <Column
                        cell={DeleteCell}
                        width="60px"
                    />
                </Grid>
            </GridWrapper>
            <div className={styles.panelPilotCertificateButtons}>
                <Button
                    themeColor="primary"
                    onClick={handleSave}
                    disabled={!canSave}>{buttonText}</Button>
            </div>
        </div>
    )
}

export const StatusCell = React.memo<GridCellProps>(({ dataItem, tdProps }) => {

    const mapProperties = MapPropertiesService.properties;
    const fill = dataItem.status === "APPROVED" ?
        "transparent":
        mapProperties.ColorCertificateReadyForApproval;

    return (
        <td {...tdProps} style={{ background: fill }}>
        </td>
    );
});
StatusCell.displayName = "StatusCell";

export const NameCell = React.memo<GridCellProps>(({ dataItem, tdProps }) => {
    const setFeatureMouseOver = useSetFeatureMouseOver();
    const setSelectedPilotCoastalSegmentGroup = useSetSelectedPilotCoastalSegmentGroup();
    return (
        <td {...tdProps}
            onMouseOver={() => setFeatureMouseOver(dataItem)}
            onMouseOut={() => setFeatureMouseOver(null)}
            onClick={() => setSelectedPilotCoastalSegmentGroup(dataItem)}>
            <div>{dataItem.name}</div>
        </td>
    );
});
NameCell.displayName = "NameCell";

export const DeleteCell = React.memo<GridCellProps>(({ dataItem, tdProps }) => {

    const setSelectedCoastalSegmentGroup = useSetSelectedCoastalSegmentGroup();

    const onClick = useCallback(() => {
        setSelectedCoastalSegmentGroup({
            id: dataItem.id,
            isSelected: false
        });
    }, [dataItem, setSelectedCoastalSegmentGroup]);

    return (
        <td {...tdProps}>
            <div>
                <Button onClick={onClick}>
                    <IconPicker icon="trash" />
                </Button>
            </div>
        </td>
    );
});
DeleteCell.displayName = "DeleteCell";
